import {Platform} from 'react-native';

type ShareOptions = {
  title?: string;
  message?: string;
  url?: string;
  subject?: string;
  email?: string;
  recipient?: string;
  social?: string;
  forceDialog?: boolean;
  failOnCancel?: boolean;
  showAppsToView?: boolean;
  [key: string]: any;
};

class WebShare {
  // Check if the Web Share API is available
  static isAvailable(): boolean {
    return Platform.OS === 'web' && !!navigator.share;
  }

  static async open(options: ShareOptions): Promise<{success: boolean}> {
    if (Platform.OS === 'web') {
      try {
        // If Web Share API is available, use it
        if (navigator.share) {
          const shareData: {[key: string]: any} = {};

          if (options.title) shareData.title = options.title;
          if (options.message || options.subject) {
            shareData.text = options.message || options.subject;
          }
          if (options.url) shareData.url = options.url;

          await navigator.share(shareData);
          return {success: true};
        } else {
          // Fallback for browsers without Web Share API
          const textToCopy = options.url || options.message || '';
          await this.copyToClipboard(textToCopy);

          // Create a temporary popup to inform the user
          this.createCopyNotification(
            options.title || 'Share',
            'Link copied to clipboard',
          );

          return {success: true};
        }
      } catch (error) {
        return {success: false};
      }
    }

    // This shouldn't be reached in web environment
    throw new Error('WebShare is only available in web environment');
  }

  private static async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {

      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed'; // Avoid scrolling to bottom
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (err) {
      }

      document.body.removeChild(textArea);
    }
  }

  private static createCopyNotification(title: string, message: string): void {
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.left = '50%';
    notification.style.transform = 'translateX(-50%)';
    notification.style.backgroundColor = '#333';
    notification.style.color = 'white';
    notification.style.padding = '10px 20px';
    notification.style.borderRadius = '4px';
    notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    notification.style.zIndex = '9999';
    notification.style.transition = 'opacity 0.5s';

    const titleElement = document.createElement('div');
    titleElement.style.fontWeight = 'bold';
    titleElement.style.marginBottom = '5px';
    titleElement.textContent = title;

    const messageElement = document.createElement('div');
    messageElement.textContent = message;

    notification.appendChild(titleElement);
    notification.appendChild(messageElement);
    document.body.appendChild(notification);

    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 500);
    }, 3000);
  }
}

export default WebShare;
